<template>
  <v-card class="data-table-toolbar pa-2" variant="outlined" elevation="0">
    <v-row align="center">
      <v-col>
        <h2 class="text-indigo pl-2 pt-2">Filter By</h2>
      </v-col>
      <v-col cols="auto">
        <v-btn-group
          divided
          variant="outlined"
          multiple
          class="rounded-xl"
          tile
        >
          <v-btn
            :disabled="!reportingState.activeFilterCount"
            data-pw="resetFilterButton"
            @click="handleResetFilter"
          >
            <v-icon class="mr-2">far fa-refresh</v-icon>
            Reset
          </v-btn>
          <v-btn
            @click="handleClickRunReport"
            class="text-indigo"
            color="indigo"
            :loading="loading"
            :disabled="loading"
          >
            Run Report
            <v-icon>far fa-chevron-right</v-icon>
          </v-btn>
        </v-btn-group>
      </v-col>

      <v-col v-if="showTimeFrameSwitch" cols="auto">

        <div class="d-flex align-center text-right">
          <v-icon class="mr-2" size="xsmall">
            far fa-calendar
          </v-icon>
          <span class="mr-2 text-body-1">Time Filter</span>
          <v-switch
            v-model="showTimeFilter"
            hide-details
            class="pl-2"
            color="blue"
          />
        </div>
      </v-col>
    </v-row>

    <v-card-title class="d-flex align-center">
    </v-card-title>

    <v-divider class="mb-2" />

    <v-row v-if="showTimeFrame || showTimeFilter" no-gutters class="pl-1 pr-4 my-2" align-content="center"
           justify="space-between">
      <v-btn-toggle density="compact" class="mr-2">
        <v-menu :close-on-content-click="false">
          <template v-slot:activator="{ props }">
            <v-btn
              size="small"
              variant="flat"
              v-bind="props"
              prepend-icon="fa fa-calendar"
            >
              {{ $t("buttons.custom") }}
            </v-btn>
          </template>
          <v-card min-width="200px" rounded variant="outlined" class="mt-1">
            <VueDatePicker
              v-model="customDateFilter"
              range
              inline
              auto-apply
              multi-calendars
              :enable-time-picker="false"
            />
          </v-card>
        </v-menu>
        <v-btn
          size="small"
          @click="reportingState.setDateFilter('since', 'start_of_current_day', 'hours' )"
          :active="reportingState.isDateFilterActive('since', 'start_of_current_day', 'hours')"
          :class="reportingState.isDateFilterActive('since', 'start_of_current_day', 'hours') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.today') }}
        </v-btn>
        <v-btn
          size="small"
          @click="reportingState.setDateFilter('between', ['start_of_previous_day', 'end_of_previous_day'], 'hours' )"
          :active="reportingState.isDateFilterActive('between', ['start_of_previous_day', 'end_of_previous_day'], 'hours')"
          :class="reportingState.isDateFilterActive('between',
                                                    ['start_of_previous_day', 'end_of_previous_day'], 'hours')
            ? 'font-weight-bold text-primary'
            : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.yesterday') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '7', 'days' )"
          :active="reportingState.isDateFilterActive('in the last', '7', 'days')"
          :class="reportingState.isDateFilterActive('in the last', '7', 'days') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.7D') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '30', 'days' )"
          :active="reportingState.isDateFilterActive('in the last', '30', 'days')"
          :class="reportingState.isDateFilterActive('in the last', '30', 'days') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.30D') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '3', 'months' )"
          :active="reportingState.isDateFilterActive('in the last', '3', 'months')"
          :class="reportingState.isDateFilterActive('in the last', '3', 'months') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.3M') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '6', 'months' )"
          :active="reportingState.isDateFilterActive('in the last', '6', 'months')"
          :class="reportingState.isDateFilterActive('in the last', '6', 'months') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.6M') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '12', 'months' )"
          :class="reportingState.isDateFilterActive('in the last', '12', 'months') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.12M') }}
        </v-btn>
        <v-btn
          @click="reportingState.setDateFilter('in the last', '24', 'months' )"
          :class="reportingState.isDateFilterActive('in the last', '24', 'months') ? 'font-weight-bold text-primary' : ''"
        >
          {{ $t('components.ReportPeriodFilterBar.24M') }}
        </v-btn>
      </v-btn-toggle>
    </v-row>

    <v-divider v-if="showTimeFrame" class="py-2"/>

    <div v-if="showFilters" class="px-2">
      <v-row no-gutters class="pl-2 pb-4">
        <v-col v-if="reportingState.availableFilters.tenants && (canReadTenants || isAdminOrRoot)" cols="4">
          <h4 class="pb-2 text-grey-darken-2">{{ $t("common.entities.Tenant") }}</h4>
          <tenant-search-autocomplete
            v-model="selectedTenant"
            entity-type="tenant"
            :multiple="true"
          />
        </v-col>
        <v-col v-if="reportingState.availableFilters.institutions
          && (canReadTenants || isAdminOrRoot || canReadInstitutions)" cols="4" class="pr-4 pl-2">
          <h4 class="pb-2 text-grey-darken-2">{{ $t("common.entities.Institution") }}</h4>
          <tenant-search-autocomplete
            v-model="selectedInstitution"
            entity-type="institution"
            :tenant-ids="selectedTenant ? selectedTenant : []"
            :multiple="true"
          />
        </v-col>
        <v-col v-if="reportingState.availableFilters.cohorts" cols="4" class="pr-4">
          <h4 class="pb-2 text-grey-darken-2">{{ $t("common.entities.Cohort") }}</h4>
          <tenant-search-autocomplete
            v-model="selectedCohort"
            entity-type="cohort"
            :multiple="true"
            :tenant-ids="selectedTenant ? selectedTenant : []"
            :institution-ids="selectedInstitution ? selectedInstitution : []"
          />
        </v-col>
        <v-col v-if="reportingState.availableFilters.curriculum" cols="4" class="pr-4">
          <h4 class="pb-2">Curriculum</h4>
          <curriculum-select-dropdown v-model="reportingState.filters.curriculum"/>
        </v-col>
      </v-row>
    </div>

  </v-card>
</template>

<script setup lang="ts">
import {onMounted, ref, watch} from "vue";
import VueDatePicker from "@vuepic/vue-datepicker";
import CurriculumSelectDropdown from "@/components/FormFields/CurriculumSelectDropdown.vue";
import {useReportingStore} from "@/store/reporting";
import {useGlobalStore} from "@/store";
import {Action, Entity, Role} from "@YenzaCT/sdk";
import TenantSearchAutocomplete from "@/components/FormFields/TenantSearchAutocomplete.vue";

const store = useGlobalStore();
const reportingState = useReportingStore();
const isAdminOrRoot = ref(store.user?.app.role === Role.Admin || store.user?.app.role === Role.Root);
const canReadTenants = store.hasAbility(Entity.Tenant, Action.read);
const canReadInstitutions = store.hasAbility(Entity.Institution, Action.read);
const selectedTenant = ref<string[] | null>();
const selectedInstitution = ref<string[] | null>();
const selectedCohort = ref<string[] | null>();

const customDateFilter = ref();
const showTimeFilter = ref<boolean>(false);

defineProps({
  showFilters: {
    type: Boolean,
    default: true
  },
  loading: {
    type: Boolean,
    default: false
  },
  showTimeFrame: {
    type: Boolean,
    default: true
  },
  showTimeFrameSwitch: {
    type: Boolean,
    default: false
  },
});

function handleResetFilter() {
  reportingState.resetFilters();
  selectedTenant.value = null;
  selectedInstitution.value = null;
  selectedCohort.value = null;
  showTimeFilter.value = false;
  emit("resetFilters");
}

function handleClickRunReport() {
  reportingState.setTenants(selectedTenant.value);
  reportingState.setInstitutions(selectedInstitution.value);
  reportingState.setCohorts(selectedCohort.value);
  emit("runReport");
}

onMounted(() => {
  reportingState.resetFilters();
});

const emit = defineEmits<{
  (e: "resetFilters"): void;
  (e: "runReport"): void;
}>();

const showCustomDatePicker = ref(false);

watch(() => reportingState.filters.dateFilter, (newFilter) => {
  if (newFilter !== "range") {
    customDateFilter.value = [];
  }
});
watch(() => customDateFilter.value, (newDates) => {
  if (newDates.length > 0) {
    reportingState.setDateFilter("range", newDates, "dates");
  }
  showCustomDatePicker.value = false;
});
</script>
<style scoped>
</style>
